import { cn } from "@faire/prototype-playground/lib/utils";
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * A stand-in for a generated slideshow video: the product's first few
 * static images played back-to-back on a fixed cadence with a crossfade, so
 * a product with no uploaded video can still fill a video ad tile.
 */
export type SlideshowAsset = {
  imageUrls: string[];
  /** How long each image holds before the next fade starts. */
  slideDurationMs: number;
  /** Length of the crossfade between two images. */
  fadeMs: number;
};

export const SLIDESHOW_IMAGE_COUNT = 4;
export const SLIDESHOW_SLIDE_DURATION_MS = 2000;
export const SLIDESHOW_FADE_MS = 500;

/** Hero image first, then the product's real gallery, capped at `SLIDESHOW_IMAGE_COUNT`. */
export const getSlideshowImageUrls = (product: {
  imageUrl: string;
  galleryImageUrls?: string[];
}): string[] => {
  const unique = new Set<string>();
  for (const url of [product.imageUrl, ...(product.galleryImageUrls ?? [])]) {
    if (url) unique.add(url);
  }
  return Array.from(unique).slice(0, SLIDESHOW_IMAGE_COUNT);
};

export const canBuildSlideshowAsset = (product: {
  imageUrl: string;
  galleryImageUrls?: string[];
}): boolean => getSlideshowImageUrls(product).length >= SLIDESHOW_IMAGE_COUNT;

export const buildSlideshowAsset = (
  product: { imageUrl: string; galleryImageUrls?: string[] },
  overrides: Partial<Omit<SlideshowAsset, "imageUrls">> = {}
): SlideshowAsset => ({
  imageUrls: getSlideshowImageUrls(product),
  slideDurationMs: SLIDESHOW_SLIDE_DURATION_MS,
  fadeMs: SLIDESHOW_FADE_MS,
  ...overrides,
});

export type SlideshowPlaybackIndicator = "none" | "dots" | "bars";

/**
 * Plays a `SlideshowAsset` like a muted, looping, autoplaying video: every
 * image is mounted (so they're all decoded before their turn) and only the
 * active one is opaque. `playing` mirrors a video's play/pause — the
 * cadence stops while false and resumes from the same image.
 */
export const SlideshowVideo = ({
  asset,
  alt,
  playing = true,
  playbackIndicator = "none",
  className,
}: {
  asset: SlideshowAsset;
  alt: string;
  playing?: boolean;
  /** Resting playback affordance along the bottom of the slideshow. */
  playbackIndicator?: SlideshowPlaybackIndicator;
  className?: string;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const imagesKey = asset.imageUrls.join("|");

  useEffect(() => {
    setActiveIndex(0);
  }, [imagesKey]);

  useEffect(() => {
    if (!playing || asset.imageUrls.length <= 1) return undefined;
    const intervalId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % asset.imageUrls.length);
    }, asset.slideDurationMs);
    return () => window.clearInterval(intervalId);
  }, [playing, imagesKey, asset.imageUrls.length, asset.slideDurationMs]);

  return (
    <div className={cn("absolute inset-0 h-full w-full", className)}>
      {asset.imageUrls.map((url, index) => (
        <img
          key={url}
          src={url}
          alt={index === 0 ? alt : ""}
          className="absolute inset-0 h-full w-full object-cover ease-in-out"
          style={{
            opacity: index === activeIndex ? 1 : 0,
            transitionProperty: "opacity",
            transitionDuration: `${asset.fadeMs}ms`,
          }}
          referrerPolicy="no-referrer"
          draggable={false}
        />
      ))}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "rgba(51,51,51,0.03)" }}
      />
      {playbackIndicator !== "none" && asset.imageUrls.length > 1 ? (
        <SlideshowPlaybackChrome
          imageCount={asset.imageUrls.length}
          activeIndex={activeIndex}
          slideDurationMs={asset.slideDurationMs}
          playing={playing}
          variant={playbackIndicator}
        />
      ) : null}
    </div>
  );
};

const SlideshowPlaybackChrome = ({
  imageCount,
  activeIndex,
  slideDurationMs,
  playing,
  variant,
}: {
  imageCount: number;
  activeIndex: number;
  slideDurationMs: number;
  playing: boolean;
  variant: Exclude<SlideshowPlaybackIndicator, "none">;
}) => {
  if (variant === "dots") {
    return (
      <div className="pointer-events-none absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5">
        {Array.from({ length: imageCount }, (_, index) => (
          <span
            key={index}
            className="block h-1.5 w-1.5 rounded-full transition-colors duration-150"
            style={{
              backgroundColor:
                index === activeIndex ? "#ffffff" : "rgba(255, 255, 255, 0.5)",
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <PlaybackBarStrip>
      {Array.from({ length: imageCount }, (_, index) => {
        const isPast = index < activeIndex;
        const isCurrent = index === activeIndex;
        return (
          <PlaybackBarTrack key={index}>
            <div
              key={isCurrent ? `${activeIndex}-${slideDurationMs}` : index}
              className={PLAYBACK_BAR_FILL_CLASS}
              style={{
                ...PLAYBACK_BAR_FILL_STYLE,
                transform: isPast
                  ? "scaleX(1)"
                  : isCurrent
                    ? undefined
                    : "scaleX(0)",
                animation: isCurrent
                  ? `slideshow-bar-fill ${slideDurationMs}ms linear forwards`
                  : undefined,
                animationPlayState: playing ? "running" : "paused",
              }}
            />
          </PlaybackBarTrack>
        );
      })}
      <style>
        {`@keyframes slideshow-bar-fill { from { transform: scaleX(0); } to { transform: scaleX(1); } }`}
      </style>
    </PlaybackBarStrip>
  );
};

/**
 * The "Bars" indicator for a real video: one track spanning the same width
 * as the slideshow's 4-segment strip, filled to the video's current position.
 * Progress is written straight to the DOM each frame so a 60fps video doesn't
 * re-render the tile.
 */
export const VideoPlaybackBar = ({
  videoRef,
  className,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  className?: string;
}) => {
  const fillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frameId = 0;
    const tick = () => {
      const video = videoRef.current;
      const fill = fillRef.current;
      if (video && fill) {
        const progress =
          Number.isFinite(video.duration) && video.duration > 0
            ? video.currentTime / video.duration
            : 0;
        fill.style.transform = `scaleX(${progress})`;
      }
      frameId = window.requestAnimationFrame(tick);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [videoRef]);

  return (
    <PlaybackBarStrip className={className}>
      <PlaybackBarTrack>
        <div
          ref={fillRef}
          className={PLAYBACK_BAR_FILL_CLASS}
          style={{ ...PLAYBACK_BAR_FILL_STYLE, transform: "scaleX(0)" }}
        />
      </PlaybackBarTrack>
    </PlaybackBarStrip>
  );
};

const PLAYBACK_BAR_FILL_CLASS = "h-full origin-left rounded-full";
const PLAYBACK_BAR_FILL_STYLE = { backgroundColor: "#ffffff", width: "100%" };

/**
 * Sits below the Find similar button (which spans 8–40px from the bottom
 * and widens to 124px on hover) so the expanded pill never covers it.
 * Inset 48px per side to clear the resting button and stay centered.
 */
const PlaybackBarStrip = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "pointer-events-none absolute right-12 bottom-[3px] left-12 z-10 flex gap-[3px]",
      className
    )}
  >
    {children}
  </div>
);

const PlaybackBarTrack = ({ children }: { children: ReactNode }) => (
  <div
    className="h-[2px] min-w-0 flex-1 overflow-hidden rounded-full"
    style={{ backgroundColor: "rgba(255, 255, 255, 0.35)" }}
  >
    {children}
  </div>
);
